import { SortDirectionType, SortFieldDisplayProps } from '@components/design/migrated/Table';
import {
  CategoryLeaderboardPg,
  ColumnHeaderForCL,
  ProductGroupKpiPercentage,
  SortKeys,
  SortLabels,
} from '@models/CategoryLeaderboard';
import { formatKpi } from './Formatters';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { GroupState, LeaderboardKpi, PercentageKpi } from '@models/Products';
import { displayOption } from '@data-visualization/utilities/CommonOptions';
import { ColumnHeaderForPA } from '@models/PerformanceAnalysisboard';
import { authentication } from '@ecosystem/newron-client-lib';
import { User } from '@ecosystem/newron-client-lib/build/authentication/types';

const sortAbsoluteDesc = {
  sortKey: SortKeys.Absolute,
  label: SortLabels.Absolute,
  sortDirection: SortDirectionType.desc,
};

const sortAbsoulteAsc = {
  sortKey: SortKeys.Absolute,
  label: SortLabels.Absolute,
  sortDirection: SortDirectionType.asc,
};

const sortChangeDesc = {
  sortKey: SortKeys.Change,
  label: SortLabels.Change,
  sortDirection: SortDirectionType.desc,
};

const sortChangeAsc = {
  sortKey: SortKeys.Change,
  label: SortLabels.Change,
  sortDirection: SortDirectionType.asc,
};

export const sortFields: SortFieldDisplayProps[] = [sortAbsoulteAsc, sortAbsoluteDesc, sortChangeAsc, sortChangeDesc];
export const sortFieldsDisabled: SortFieldDisplayProps[] = [sortAbsoluteDesc, sortChangeDesc];
export const absoluteSortFields: SortFieldDisplayProps[] = [sortAbsoluteDesc, sortAbsoulteAsc];

export const isKpiShare = (kpiKey: any) => {
  return (
    kpiKey === PercentageKpi.marketShareRevenue ||
    kpiKey === PercentageKpi.marketShareVolume ||
    kpiKey === PercentageKpi.retailerShareRevenue ||
    kpiKey === PercentageKpi.retailerShareVolume ||
    kpiKey === PercentageKpi.retailerMixRevenue ||
    kpiKey === PercentageKpi.retailerMixVolume ||
    kpiKey === ProductGroupKpiPercentage.YourRevenueShare ||
    kpiKey === ProductGroupKpiPercentage.YourVolumeShare ||
    kpiKey === ColumnHeaderForPA.yourRevenueShare ||
    kpiKey === ColumnHeaderForPA.yourVolumeShare ||
    kpiKey === ColumnHeaderForCL.yourVolumeMix ||
    kpiKey === ColumnHeaderForCL.yourRevenueMix
  );
};

export const getSeries = (
  responseData: (CategoryLeaderboardPg | LeaderboardKpi)[],
  kpiKey: any,
  selectedGrowthType: GrowthTypes,
  isKpiPercentage: (kpiKey: any) => boolean,
) => {
  return [
    {
      name: 'Series 1',
      data: responseData.map((productGroup: any) => {
        const percentageValue = productGroup[kpiKey]?.percentage ?? 0;
        let leftValue: number = productGroup[kpiKey]?.value;
        let rightValue: number =
          selectedGrowthType === GrowthTypes.actual ? productGroup[kpiKey]?.actual : percentageValue;

        if (isKpiPercentage(kpiKey)) {
          rightValue = productGroup[kpiKey]?.growthPercentage;
          leftValue = productGroup[kpiKey]?.sharePercentage;
        }
        return {
          leftValue,
          rightValue,
          color: '#2b4c9b',
        };
      }),
    },
  ];
};

export const escapeAngledBrackets = (label: string) => {
  return label.replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export const labelFormatter = (label: string, numberOfChars: number) => {
  const numberOfCharsInLabel = label.length;
  if (numberOfCharsInLabel > numberOfChars) {
    return escapeAngledBrackets(`${label.substring(0, 30)}...`);
  }
  return escapeAngledBrackets(label);
};

export const getFormattedChangeValue = (
  kpiKey: any,
  changeValue: number,
  selectedGrowthType: GrowthTypes,
  currencySymbol: string,
) => {
  return formatKpi(kpiKey, changeValue, {
    currencySymbol: currencySymbol,
    as: displayOption(kpiKey, selectedGrowthType),
  });
};

export const getValueAndChangeValues = (
  kpiKey: any,
  selectedGrowthType: GrowthTypes,
  isKpiPercentage: (kpiKey: any) => boolean,
  row: any = {},
) => {
  let changeValue: number =
    selectedGrowthType === GrowthTypes.actual ? row?.[kpiKey]?.actual : row?.[kpiKey]?.percentage;
  let value: number = row?.[kpiKey]?.value;
  if (isKpiPercentage(kpiKey)) {
    value = row?.[kpiKey]?.sharePercentage;
    changeValue = row[kpiKey]?.growthPercentage;
  }
  return { changeValue: changeValue, value: value };
};

export const isShareGroupButtonDisable = (groupState: GroupState): boolean => {
  const user: User | null = authentication.getUser();

  // If no user is found, disable the button.
  if (!user) return true;

  // If the user isn't the owner, the button should be disabled
  if (!groupState.isPreferenceOwner) return true;

  // No matter internal or normal users are allow to share group
  return false;
};
